import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowRightCircle,
    Users,
    HeartHandshake,
    Mail,
    Star,
    Check,
    GraduationCap,
    Calendar,
    Linkedin,
    Phone,

} from 'lucide-react';
import {
    Therapist,
    getServiceBySlug,
    getTherapistsForService,
    TherapistPricing,
    Service
} from '../components/data/services';
import { Helmet } from 'react-helmet';


interface ServiceSeoMetadataProps {
    service: Service;
}

export const ServiceSeoMetadata: React.FC<ServiceSeoMetadataProps> = ({ service }) => {
    const seoDescriptions: Record<string, string> = {
        'individual-therapy': 'Personalized counseling for anxiety, depression, and personal growth. Expert therapist guiding your mental wellness journey. Book your session: 8591049845.',

        'couples-therapy': 'Rebuild trust and communication in your relationship. Professional guidance for resolving conflicts and deepening connections. Call 8591049845 today.',

        'family-therapy': 'Create harmony in your family dynamics. Expert counseling for better communication and understanding between family members. Book now: 8591049845.'
    };

    const description = seoDescriptions[service.slug] || service.description;

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{`${service.name} | Mental Health Support | Greh Pravesh Therapy`}</title>
            <meta name="description" content={description} />

            {/* Open Graph Tags */}
            <meta property="og:title" content={`${service.name} | Greh Pravesh Therapy`} />
            <meta property="og:description" content={description} />
            <meta property="og:type" content="service" />

            {/* Twitter Card Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={`${service.name} | Mental Health Support`} />
            <meta name="twitter:description" content={description} />

            {/* JSON-LD Schema Markup */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Service",
                    "name": service.name,
                    "description": description,
                    "provider": {
                        "@type": "MedicalBusiness",
                        "name": "Greh Pravesh Therapy",
                        "speciality": "Mental Health Services"
                    },
                    "offers": {
                        "@type": "Offer",
                        "price": service.pricing.basePrice,
                        "priceCurrency": service.pricing.currency
                    }
                })}
            </script>
        </Helmet>
    );
};

const PricingDisplay: React.FC<{ pricing: TherapistPricing }> = ({ pricing }) => {
    return (
        <div className="space-y-2">
            <h3 className="font-semibold text-charcol mb-2 text-sm sm:text-base">Pricing</h3>

            {/* Standard Fees */}
            <div className="space-y-1">
                {/* pricing.standardFees.individual && (
                    <div className="flex items-center text-gray-700 text-sm sm:text-base">
                        <span className="font-medium">Individual Session:</span>
                         <span className="ml-2">
                            ₹{pricing.standardFees.individual.amount}
                            {pricing.standardFees.individual.location && 
                                ` (${pricing.standardFees.individual.location})`}
                        </span> */}
                {/* </div>
                )} */}

                {/* {pricing.standardFees.couples && (
                    <div className="flex items-center text-gray-700 text-sm sm:text-base">
                        <span className="font-medium">Couples Session:</span>
                        <span className="ml-2">
                            ₹{pricing.standardFees.couples.amount}
                            {pricing.standardFees.couples.location && 
                                ` (${pricing.standardFees.couples.location})`}
                        </span>
                    </div>
                )} */}
            </div>

            {/* Sliding Scale */}
            {pricing.slidingScale && (
                <div className="mt-2 text-sm sm:text-base text-gray-600">
                    <div className="font-medium">Sliding Scale Available:</div>
                    {pricing.slidingScale.range && (
                        <div>Range: ₹{pricing.slidingScale.range.min} - ₹{pricing.slidingScale.range.max}</div>
                    )}
                    {pricing.slidingScale.fixedAmount && (
                        <div>
                            {pricing.slidingScale.slots
                                ? `${pricing.slidingScale.slots} slots available at ₹${pricing.slidingScale.fixedAmount}`
                                : `₹${pricing.slidingScale.fixedAmount}`}
                        </div>
                    )}
                    {pricing.slidingScale.availableFor && (
                        <div className="text-gray-500 text-sm">
                            Available for: {pricing.slidingScale.availableFor.join(', ')}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};


const ServiceTemplatePage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [selectedTherapist, setSelectedTherapist] = useState<Therapist | null>(null);

    const navigate = useNavigate();

    // Get service and its therapists using the new helper functions
    const service = slug ? getServiceBySlug(slug) : undefined;
    const therapists = service ? getTherapistsForService(service.id) : [];

    if (!service) {
        return (
            <div className="bg-softwhite min-h-screen flex items-center justify-center text-center p-4">
                <h1 className="text-lg sm:text-xl md:text-2xl text-charcol">Service Not Found</h1>
            </div>
        );
    }

    return (
        <>
            <ServiceSeoMetadata service={service} />
            <main className="flex-grow">
                <motion.section
                    key={service.id}
                    className="flex justify-center mt-16 sm:mt-20 md:mt-24 min-h-screen"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                >
                    <div className="mb-8 sm:mb-12 relative shadow-md rounded-2xl sm:rounded-[40px] w-[98%] sm:w-[95%] max-w-full overflow-hidden">
                        {/* Hero Image */}
                        <div
                            className="w-full h-[60vh] sm:h-[70vh] md:h-[80vh] lg:h-[90vh] bg-cover bg-center"
                            style={{
                                backgroundImage: `url(${service.heroImage})`,
                                backgroundBlendMode: 'multiply',
                                backgroundColor: 'rgba(0,0,0,0.5)'
                            }}
                        />

                        <motion.div
                            className="absolute inset-0 bg-charcol bg-opacity-60 flex flex-col justify-end items-center text-center p-4 sm:p-6 md:p-8 lg:p-12 pb-8 sm:pb-12 md:pb-16 lg:pb-20"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4, duration: 0.6 }}
                        >
                            {/* Service Info */}
                            <motion.h1
                                className="text-3xl sm:text-2xl md:text-3xl lg:text-4xl mt-4 sm:mt-8 md:mt-12 mb-3 sm:mb-4 text-palegola font-bold"
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.6, duration: 0.6 }}
                            >
                                {service.name}
                            </motion.h1>

                            <motion.p
                                className="text-md sm:text-base md:text-lg text-softwhite mb-4 sm:mb-6 max-w-xs sm:max-w-sm md:max-w-xl lg:max-w-2xl"
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.8, duration: 0.6 }}
                            >
                                {service.description}
                            </motion.p>

                            {/* Service Info Cards */}
                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
                                {[
                                    { icon: Users, text: `${service.sessionsPerMonth} Sessions/Month` },
                                    { icon: HeartHandshake, text: `${service.duration} Duration` }
                                ].map((item, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-center bg-softwhite/10 px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl text-softwhite"
                                    >
                                        <item.icon className="mr-2 text-palegola w-4 h-4 sm:w-5 sm:h-5" />
                                        <span className="text-xs sm:text-sm">{item.text}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Therapist Selection */}
                            <div className="mb-4 sm:mb-6 w-full max-w-xl sm:max-w-2xl">
                                <h3 className="text-lg sm:text-xl text-softwhite mb-3 sm:mb-4">Choose Your Therapist</h3>
                                <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
                                    {therapists.map((therapist) => (
                                        <motion.div
                                            key={therapist.id}
                                            className={`
                                            relative cursor-pointer rounded-lg sm:rounded-xl overflow-hidden shadow-lg
                                            ${selectedTherapist?.id === therapist.id
                                                    ? 'ring-2 sm:ring-4 ring-golden-yellow'
                                                    : 'hover:opacity-80'}
                                        `}
                                            whileHover={{ scale: 1.05 }}
                                            onClick={() => setSelectedTherapist(therapist)}
                                        >
                                            <img
                                                src={therapist.avatar}
                                                alt={therapist.name}
                                                className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-cover"
                                            />
                                            {selectedTherapist?.id === therapist.id && (
                                                <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-golden-yellow text-charcol rounded-full p-1">
                                                    <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                                                </div>
                                            )}
                                        </motion.div>
                                    ))}
                                </div>
                            </div>

                            {/* Button and Pricing */}
                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center">
                                <motion.button
                                    className={`
                                    bg-golden-yellow text-charcol font-semibold px-4 sm:px-6 py-2 sm:py-3 
                                    rounded-full flex items-center transition-colors text-sm sm:text-base
                                    ${selectedTherapist
                                            ? 'hover:bg-palegola'
                                            : 'opacity-50 cursor-not-allowed'}
                                `}
                                    disabled={!selectedTherapist}
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: selectedTherapist ? 1 : 0.5 }}
                                    transition={{ delay: 1, duration: 0.6 }}
                                    whileHover={{ scale: selectedTherapist ? 1.05 : 1 }}
                                    whileTap={{ scale: selectedTherapist ? 0.95 : 1 }}
                                >
                                    Book Appointment
                                    <ArrowRightCircle className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                                </motion.button>

                                <motion.div
                                    className="text-xs sm:text-sm text-softwhite/70"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 1.2, duration: 0.6 }}
                                >
                                    Starting at {service.pricing.currency} {service.pricing.basePrice}
                                </motion.div>
                            </div>
                        </motion.div>
                    </div>
                </motion.section>
                {/* Updated Modal */}
                {selectedTherapist && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999] p-3 sm:p-4 overflow-y-auto"
                        onClick={() => setSelectedTherapist(null)}
                    >
                        <motion.div
                            className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 max-w-[95%] sm:max-w-2xl w-full my-4 sm:my-8 relative max-h-[90vh] overflow-y-auto"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex flex-col items-center">
                                <img
                                    src={selectedTherapist.avatar}
                                    alt={selectedTherapist.name}
                                    className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover mb-3 sm:mb-4 border-2 sm:border-4 border-golden-yellow"
                                />
                                <div className="text-center mb-4">
                                    <h2 className="text-xl sm:text-2xl font-bold text-charcol">
                                        {selectedTherapist.name}
                                    </h2>
                                    <p className="text-gray-600 text-sm sm:text-base">{selectedTherapist.pronouns}</p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full mb-6">
                                    {/* Left Column */}
                                    <div className="space-y-4">
                                        {/* Experience */}
                                        <div className="flex items-center text-palegola text-sm sm:text-base">
                                            <Star className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                                            <span>{selectedTherapist.experience} Years Experience</span>
                                        </div>

                                        <PricingDisplay pricing={selectedTherapist.pricing} />


                                        {/* Qualifications */}
                                        <div>
                                            <div className="flex items-center mb-2 text-charcol">
                                                <GraduationCap className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                                                <h3 className="font-semibold text-sm sm:text-base">Qualifications</h3>
                                            </div>
                                            <div className="ml-6 space-y-1">
                                                {selectedTherapist.qualifications.map((qual, idx) => (
                                                    <div key={idx} className="text-gray-600 text-sm">
                                                        <p className="font-medium">{qual.degree}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Availability */}
                                        <div>
                                            <div className="flex items-center mb-2 text-charcol">
                                                <Calendar className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                                                <h3 className="font-semibold text-sm sm:text-base">Availability</h3>
                                            </div>
                                            {/* <div className="ml-6 text-gray-600 text-sm">
                                            <p>Days: {selectedTherapist.availability.days}</p>
                                            <p>Hours: {selectedTherapist.availability.hours}</p>
                                        </div> */}
                                        </div>
                                    </div>

                                    {/* Right Column */}
                                    <div className="space-y-4">
                                        {/* Languages */}
                                        <div>
                                            <h3 className="font-semibold text-charcol mb-2 text-sm sm:text-base">Languages</h3>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedTherapist.languages.map((lang, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="bg-softwhite border border-charcol/20 text-charcol px-2 py-1 rounded-full text-xs sm:text-sm"
                                                    >
                                                        {lang}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        {/* Specialization */}
                                        <div>
                                            <h3 className="font-semibold text-charcol mb-2 text-sm sm:text-base">Specialization</h3>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedTherapist.specialization.map((spec, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="bg-palegola/10 text-charcol px-2 py-1 rounded-full text-xs sm:text-sm"
                                                    >
                                                        {spec}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Bio */}
                                <div className="w-full mb-6">
                                    <h3 className="font-semibold text-charcol mb-2 text-sm sm:text-base">About</h3>
                                    <p className="text-gray-600 text-sm sm:text-base">{selectedTherapist.bio}</p>
                                </div>

                                {/* Contact Information */}
                                <div className="w-full space-y-2 mb-6">
                                    <div className="flex items-center text-gray-700 text-sm sm:text-base">
                                        <Mail className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                                        <span>{selectedTherapist.contact.email}</span>
                                    </div>
                                    {selectedTherapist.contact.phone && (
                                        <div className="flex items-center text-gray-700 text-sm sm:text-base">
                                            <Phone className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                                            <span>{selectedTherapist.contact.phone}</span>
                                        </div>
                                    )}
                                    <a
                                        href={selectedTherapist.contact.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center text-blue-600 hover:text-blue-700 text-sm sm:text-base"
                                    >
                                        <Linkedin className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                                        <span>LinkedIn Profile</span>
                                    </a>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full">
                                    <button
                                        className="bg-golden-yellow text-charcol font-semibold px-4 sm:px-6 py-2 sm:py-3 rounded-full flex items-center justify-center transition-colors hover:bg-palegola w-full text-sm sm:text-base"
                                        onClick={() => navigate('/book-appointment')}
                                    >
                                        Book Appointment
                                        <ArrowRightCircle className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                                    </button>
                                    <button
                                        className="bg-softwhite border border-charcol/20 text-charcol font-semibold px-4 sm:px-6 py-2 sm:py-3 rounded-full flex items-center justify-center transition-colors hover:bg-softwhite/80 w-full text-sm sm:text-base"
                                        onClick={() => setSelectedTherapist(null)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </main>
        </>
    );
};

export default ServiceTemplatePage;
